import { GAME_VERSION } from "./version";
import { inAndroidApp } from "./app-shell";

// Error reports from players' devices, to Sentry: crashes of the page (a render
// error, an uncaught exception, a rejected promise) and the game's own signals
// (things that go wrong without throwing, like a death the screen cannot explain).
// The Android app and an iPhone's home-screen app are this same page, so they
// report here too, tagged by `platform`.
//
// Off without VITE_SENTRY_DSN (in apps/client/.env.production; the DSN is
// public by design, like the Supabase anon key) and on a dev machine. The SDK is
// loaded after the page is up, never in the way of the first paint; what goes
// wrong before that is held and sent once it has loaded. No names, no emails,
// no IP addresses: a report says what broke, on which build and kind of device.
const DSN = (import.meta as any).env?.VITE_SENTRY_DSN as string | undefined;
const LOCAL = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1|\[::1\]|192\.168\.|10\.)/.test(window.location.hostname);
const ON = !!DSN && typeof window !== "undefined" && (!LOCAL || (import.meta as any).env?.VITE_SENTRY_DEV === "1");

type Held = { kind: "error"; error: unknown; extra?: Record<string, unknown> } | { kind: "signal"; name: string; data?: Record<string, unknown>; level: "info" | "warning" | "error" };
let sdk: typeof import("@sentry/browser") | null = null;
const held: Held[] = [];
const MAX_HELD = 30;

function platform(): string {
  try {
    if (inAndroidApp()) return "android-app";
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: fullscreen)").matches || (navigator as any).standalone === true;
    const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const android = /Android/.test(navigator.userAgent);
    if (standalone) return ios ? "ios-installed" : android ? "android-app" : "installed";
    return ios ? "ios-browser" : android ? "android-browser" : "web";
  } catch { return "web"; }
}

function send(h: Held) {
  if (!sdk) return;
  if (h.kind === "error") sdk.captureException(h.error, h.extra ? { extra: h.extra } : undefined);
  else {
    // (whether the page was in view, searchable: a hidden tab or a phone app in
    // the background explains most network signals, so they can be split off)
    const tags: Record<string, string> = { signal: h.name };
    if (h.data && typeof h.data.visible === "boolean") tags.visible = String(h.data.visible);
    sdk.captureMessage(h.name, { level: h.level, tags, extra: h.data, fingerprint: ["signal", h.name] });
  }
}
function hold(h: Held) {
  if (!ON) return;
  if (sdk) send(h);
  else if (held.length < MAX_HELD) held.push(h);
}

// An error something caught itself (an error boundary, a try/catch that gives up).
export function reportError(error: unknown, extra?: Record<string, unknown>): void { hold({ kind: "error", error, extra }); }
// Something the game noticed that never throws. `name` groups the reports: keep it
// a fixed short string and put what varies in `data`.
export function reportSignal(name: string, data?: Record<string, unknown>, level: "info" | "warning" | "error" = "warning"): void { hold({ kind: "signal", name, data, level }); }
// Where the player is (a room kind, a page), on every later report. Never a name.
export function setMonitoringContext(tags: Record<string, string | null>): void {
  if (!ON) return;
  pendingTags = { ...pendingTags, ...tags };
  if (sdk) for (const k of Object.keys(tags)) sdk.setTag(k, tags[k] === null ? undefined : (tags[k] as string));
}
let pendingTags: Record<string, string | null> = {};

export function startMonitoring(): void {
  if (!ON) return;
  // Until the SDK is in: keep what breaks.
  const onError = (e: ErrorEvent) => hold({ kind: "error", error: e.error || new Error(e.message) });
  const onRejection = (e: PromiseRejectionEvent) => hold({ kind: "error", error: e.reason instanceof Error ? e.reason : new Error("Unhandled rejection: " + String(e.reason)) });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  const load = () => {
    import("@sentry/browser").then((S) => {
      S.init({
        dsn: DSN,
        release: "client@" + GAME_VERSION,
        environment: LOCAL ? "dev" : /\.pages\.dev$/.test(window.location.hostname) ? "preview" : "production",
        dataCollection: { userInfo: false, cookies: false },
        tracesSampleRate: 0, // errors only: no performance traces, no session replay
        maxBreadcrumbs: 40,
        // other people's code and browser noise, nothing of ours to fix
        ignoreErrors: ["ResizeObserver loop", "Non-Error promise rejection captured", "Load failed", "Failed to fetch", "NetworkError when attempting to fetch resource", "The operation was aborted", "AbortError"],
        denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-(web-)?extension:\/\//],
        initialScope: { tags: { side: "client", platform: platform() } },
      });
      // the SDK has its own handlers now
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      sdk = S;
      for (const k of Object.keys(pendingTags)) S.setTag(k, pendingTags[k] === null ? undefined : (pendingTags[k] as string));
      held.splice(0).forEach(send);
    }).catch(() => { /* blocked or offline: the game goes on without it */ });
  };
  const idle = (window as any).requestIdleCallback as undefined | ((cb: () => void, o?: { timeout: number }) => void);
  if (idle) idle(load, { timeout: 4000 }); else window.setTimeout(load, 2500);
}
